import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    CircularProgress,
    Alert,
    IconButton,
    Chip,
    Divider,
    Link,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { SecondaryButton, PrimaryButton } from "../common/Buttons";
import {
    Close as CloseIcon,
    CloudUpload as CloudUploadIcon,
    Description as DescriptionIcon,
    Send as SendIcon,
    AttachFile as AttachFileIcon,
    CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { getDocumentTypes, uploadDocument, getDocuments } from "../../services/documents";
import { MAX_DOCUMENT_UPLOAD_MB } from "../../constants/documentUpload";
import { validateFile } from "../../utils/documentUtils";
import { CustomTextField } from "../common/CustomTextFields";
import { toast } from "react-toastify";

const hasCompanyPrimaryContactEmail = (details) =>
    !!(details && typeof details.email === "string" && details.email.trim());

const resolveQuotationDocumentType = (types) => {
    if (!Array.isArray(types)) return null;
    return types.find((t) => {
        const raw = String(t?.name ?? "").trim().toLowerCase();
        if (!raw) return false;
        if (raw === "quotation") return true;
        return raw.replace(/[\s_-]+/g, "") === "quotation";
    }) ?? null;
};

/**
 * QuotationUploadDialog - Modal for uploading/selecting quotation document during state transition to "Quoted"
 *
 * This dialog appears when the user moves a service order **into Quoted**, including
 * **Send Quotation** and **Revise Quotation** (same attachment flow).
 *
 * Flow (per service order, at send time):
 * - Default (checkbox off): Mark as Quoted with no file upload and no email; customer
 *   accepts/rejects in the portal (legacy behaviour).
 * - Checkbox on: Upload or pick a quotation PDF and email it to the customer with
 *   Accept/Reject links (current behaviour).
 */
const QuotationUploadDialog = ({
    open,
    onClose,
    serviceOrderId,
    serviceOrderDisplay,
    companyId,
    primaryContactUserDetails,
    onProceed, // Called with (documentId, { sendQuotationEmail }) to proceed with transition
    allowedFileTypes = ["pdf", "doc", "docx"],
    maxSizeMB = MAX_DOCUMENT_UPLOAD_MB
}) => {
    const [file, setFile] = useState(null);
    const [quotationTypeId, setQuotationTypeId] = useState(null);
    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState("");
    const [documentTypes, setDocumentTypes] = useState([]);
    const [existingQuotations, setExistingQuotations] = useState([]);
    const [selectedExistingDoc, setSelectedExistingDoc] = useState(null);
    const [uploadMode, setUploadMode] = useState(false); // false = use existing, true = upload new
    const [sendQuotationEmail, setSendQuotationEmail] = useState(false);

    useEffect(() => {
        if (open) {
            fetchInitialData();
        }
    }, [open, serviceOrderId]);

    const fetchInitialData = async () => {
        setInitialLoading(true);
        setError("");
        try {
            // Fetch document types
            const types = await getDocumentTypes();
            setDocumentTypes(types);

            const quotationType = resolveQuotationDocumentType(types);
            if (quotationType) {
                setQuotationTypeId(quotationType.id);

                // Fetch existing quotation documents for this service order
                const docs = await getDocuments({
                    reference_type: 'service_order',
                    reference_id: serviceOrderId,
                    document_type: quotationType.id
                });

                const quotationDocs = Array.isArray(docs) ? docs : (docs?.results || []);
                setExistingQuotations(quotationDocs);

                // If there are existing quotations, select the most recent one
                if (quotationDocs.length > 0) {
                    // Sort by created_at descending and select the first one
                    const sorted = [...quotationDocs].sort((a, b) =>
                        new Date(b.created_at) - new Date(a.created_at)
                    );
                    setSelectedExistingDoc(sorted[0]);
                    setUploadMode(false);
                } else {
                    // No existing quotations, must upload
                    setUploadMode(true);
                }
            } else {
                const msg =
                    "Quotation document type is missing. Run database migrations for the documents app, then: python manage.py setup_document_types";
                setError(msg);
                toast.error(msg);
            }
        } catch (error) {
            console.error('Error fetching initial data:', error);
            setError('Failed to load document data');
        } finally {
            setInitialLoading(false);
        }
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setError("");
        }
    };

    const handleProceedWithoutEmail = async () => {
        setLoading(true);
        setError("");
        try {
            if (onProceed) {
                onProceed(null, { sendQuotationEmail: false });
            }
            handleClose();
        } catch (error) {
            console.error('Error:', error);
            setError('Failed to send quotation');
        } finally {
            setLoading(false);
        }
    };

    const handleProceedWithExisting = async () => {
        if (!selectedExistingDoc) {
            setError("Please select a quotation document");
            return;
        }

        setLoading(true);
        try {
            if (onProceed) {
                onProceed(selectedExistingDoc.id, { sendQuotationEmail: true });
            }
            handleClose();
        } catch (error) {
            console.error('Error:', error);
            setError('Failed to proceed with quotation');
        } finally {
            setLoading(false);
        }
    };

    const handleUploadAndProceed = async () => {
        if (!file) {
            setError("Please select a quotation file");
            return;
        }

        if (!quotationTypeId) {
            setError("Quotation document type not found. Please contact support.");
            return;
        }

        // Validate file
        const validation = validateFile(file, allowedFileTypes, maxSizeMB);
        if (!validation.isValid) {
            setError(validation.error);
            return;
        }

        setLoading(true);
        setError("");

        try {
            const uploadData = {
                file: file,
                documentTypeId: quotationTypeId,
                description: description.trim() || "Quotation document",
                referenceType: "service_order",
                referenceId: serviceOrderId.toString()
            };

            const response = await uploadDocument(uploadData);

            toast.success("Quotation uploaded successfully!");

            // Proceed with transition, passing the document ID (API returns document_handle.id)
            const docId =
                response?.document_handle?.id ??
                response?.id ??
                response?.data?.id;
            if (onProceed && docId != null) {
                onProceed(docId, { sendQuotationEmail: true });
            }

            handleClose();

        } catch (error) {
            console.error('Upload error:', error.response?.data);
            const data = error.response?.data;
            let msg = null;
            if (data?.errors && typeof data.errors === 'object') {
                const parts = Object.entries(data.errors).map(([key, val]) => {
                    const text = Array.isArray(val) ? val.join(', ') : String(val);
                    return `${key}: ${text}`;
                });
                if (parts.length > 0) msg = parts.join(' ');
            }
            msg = msg
                || data?.message
                || (typeof data?.detail === 'string' ? data.detail : null)
                || data?.error
                || error.message
                || "Failed to upload quotation";
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setDescription("");
        setError("");
        setSelectedExistingDoc(null);
        setUploadMode(false);
        setExistingQuotations([]);
        setSendQuotationEmail(false);
        onClose();
    };

    const getFileSize = (bytes) => {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    const isFileValid = (file) => {
        if (!file) return false;
        const validation = validateFile(file, allowedFileTypes, maxSizeMB);
        return validation.isValid;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const hasExistingQuotations = existingQuotations.length > 0;
    const quotationRecipientBlocked = !hasCompanyPrimaryContactEmail(primaryContactUserDetails);
    const companyEditPath = companyId
        ? `/master-data/companies/${companyId}/edit`
        : "/master-data/companies";

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6" component="div">
                        Send Quotation
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                {initialLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                            Loading quotation data...
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Service Order: <strong>{serviceOrderDisplay}</strong>
                            </Typography>
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <FormControlLabel
                            sx={{ mb: 2, alignItems: 'flex-start' }}
                            control={
                                <Checkbox
                                    checked={sendQuotationEmail}
                                    onChange={(e) => {
                                        setSendQuotationEmail(e.target.checked);
                                        setError("");
                                    }}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2">
                                        Email quotation to customer (with PDF attachment)
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Leave unchecked to mark as Quoted only — the customer can accept or reject
                                        in the portal, with no file upload or email.
                                    </Typography>
                                </Box>
                            }
                        />

                        {!sendQuotationEmail && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                The service order will move to <strong>Quoted</strong>. The customer can accept or
                                reject the quotation from their account; no quotation file or email will be sent.
                            </Alert>
                        )}

                        {sendQuotationEmail && quotationRecipientBlocked && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2" gutterBottom>
                                    The customer company&apos;s&nbsp;<strong>primary contact</strong> must have an
                                    email address before a quotation can be emailed. Update it in&nbsp;
                                    <Link component={RouterLink} to={companyEditPath} underline="hover">
                                        Master Data → Companies
                                    </Link>
                                    , then return here (you may need to refresh the service order page).
                                </Typography>
                            </Alert>
                        )}

                        {/* Show existing quotation if available */}
                        {sendQuotationEmail && hasExistingQuotations && !uploadMode && (
                            <Box sx={{ mb: 3 }}>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    A quotation document is already attached. This will be sent to the customer.
                                </Alert>

                                <Box sx={{ p: 2, bgcolor: "success.50", borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                                        <CheckCircleIcon color="success" />
                                        <Typography variant="subtitle2" component="span">
                                            {selectedExistingDoc?.original_filename || selectedExistingDoc?.file?.split('/').pop() || 'Quotation Document'}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                        {selectedExistingDoc?.file_size && (
                                            <Chip
                                                label={getFileSize(selectedExistingDoc.file_size)}
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                        <Chip
                                            label={`Uploaded ${formatDate(selectedExistingDoc?.created_at)}`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>
                                    {selectedExistingDoc?.description && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                            {selectedExistingDoc.description}
                                        </Typography>
                                    )}
                                </Box>

                                <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <SecondaryButton
                                        size="small"
                                        onClick={() => setUploadMode(true)}
                                        startIcon={<CloudUploadIcon />}
                                    >
                                        Upload a different quotation
                                    </SecondaryButton>
                                </Box>
                            </Box>
                        )}

                        {/* Upload form - email path only */}
                        {sendQuotationEmail && (uploadMode || !hasExistingQuotations) && (
                            <Box>
                                {hasExistingQuotations && (
                                    <Box sx={{ mb: 2 }}>
                                        <SecondaryButton
                                            size="small"
                                            onClick={() => setUploadMode(false)}
                                        >
                                            ← Use existing quotation instead
                                        </SecondaryButton>
                                        <Divider sx={{ my: 2 }} />
                                    </Box>
                                )}

                                {!hasExistingQuotations && (
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        No quotation document attached. Please upload a quotation to send to the customer.
                                    </Alert>
                                )}

                                {/* Description Field */}
                                <CustomTextField
                                    fullWidth
                                    label="Description (Optional)"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="e.g., Quotation for NDT services"
                                    multiline
                                    rows={2}
                                    sx={{ mb: 3 }}
                                />

                                {/* File Upload */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                        Allowed types: {allowedFileTypes.join(", ").toUpperCase()} | Max size: {maxSizeMB}MB
                                    </Typography>
                                    <input
                                        accept={allowedFileTypes.map(type => `.${type}`).join(',')}
                                        style={{ display: "none" }}
                                        id="quotation-file-input"
                                        type="file"
                                        onChange={handleFileChange}
                                    />
                                    <label htmlFor="quotation-file-input">
                                        <SecondaryButton
                                            component="span"
                                            size="small"
                                            startIcon={<CloudUploadIcon />}
                                            fullWidth
                                            sx={{
                                                py: 2,
                                                borderStyle: "dashed",
                                                borderWidth: 2,
                                                "&:hover": { borderWidth: 2 },
                                            }}
                                        >
                                            {file ? "Change file" : "Select quotation file"}
                                        </SecondaryButton>
                                    </label>
                                </Box>

                                {/* File Preview */}
                                {file && (
                                    <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                                        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                                            <DescriptionIcon color="primary" />
                                            <Typography variant="subtitle2" component="span">
                                                {file.name}
                                            </Typography>
                                        </Box>
                                        <Box display="flex" gap={1} flexWrap="wrap">
                                            <Chip
                                                label={getFileSize(file.size)}
                                                size="small"
                                                variant="outlined"
                                            />
                                            <Chip
                                                label={file.name.split(".").pop().toUpperCase()}
                                                size="small"
                                                variant="outlined"
                                            />
                                            {!isFileValid(file) && (
                                                <Chip
                                                    label="Invalid file"
                                                    size="small"
                                                    color="error"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <SecondaryButton onClick={handleClose} disabled={loading}>
                    Cancel
                </SecondaryButton>

                {!sendQuotationEmail ? (
                    <PrimaryButton
                        onClick={handleProceedWithoutEmail}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                        {loading ? "Sending..." : "Send Quotation"}
                    </PrimaryButton>
                ) : hasExistingQuotations && !uploadMode ? (
                    <PrimaryButton
                        onClick={handleProceedWithExisting}
                        disabled={
                            quotationRecipientBlocked || !selectedExistingDoc || loading
                        }
                        startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                        {loading ? "Sending..." : "Send Quotation by Email"}
                    </PrimaryButton>
                ) : (
                    <PrimaryButton
                        onClick={handleUploadAndProceed}
                        disabled={
                            quotationRecipientBlocked || !file || !isFileValid(file) || loading
                        }
                        startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                        {loading ? "Uploading..." : "Upload & Email Quotation"}
                    </PrimaryButton>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default QuotationUploadDialog;
